import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbPopoverModule } from "@ng-bootstrap/ng-bootstrap";
import {
  VngButtonComponent,
  VngIconComponent,
  VngInputComponent,
  VngSelectComponent,
  VngSelectOption,
  VngTooltipDirective,
  VngTourAnchorDirective,
} from "@vengreso/angular-components";
import { SvgIconsModule } from "src/app/module/svg.module";

/**
 * Expanded-row content for a FlyMSG (FlyCut/FlyPlate) accordion row: the
 * FlyCut/Title fields + "Try it out", the Action select, the projected
 * TinyMCE editor, the autosave status line, and the Duplicate/Change
 * Category/Delete action row.
 *
 * The TinyMCE `<editor>` itself is NOT owned by this component — each host
 * keeps its own `<editor [apiKey] [init] [(ngModel)] ...>` config/autosave/
 * versioning wiring untouched and projects it in via `[editor]`.
 *
 * Values are passed as plain Inputs/Outputs (not FormControls/ngModel two-way
 * on the host side) since hosts differ in how they manage form state
 * (dashboard/shortcuts use ad-hoc `[(ngModel)]` with `standalone: true`,
 * FlyPlates renders the fields read-only).
 */
@Component({
  selector: "app-flymsg-row-editor",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbPopoverModule,
    VngButtonComponent,
    VngIconComponent,
    VngInputComponent,
    VngSelectComponent,
    VngTooltipDirective,
    VngTourAnchorDirective,
    SvgIconsModule,
  ],
  templateUrl: "./flymsg-row-editor.component.html",
  styleUrls: ["./flymsg-row-editor.component.scss"],
})
export class FlymsgRowEditorComponent {
  // FlyCut field
  @Input() flyCutValue = "";
  @Output() flyCutValueChange = new EventEmitter<string>();
  @Input() flyCutLabel = "FlyCut";
  @Input() flyCutPlaceholder = "FlyCut";
  @Input() flyCutRequired = true;
  @Input() flyCutError = "";
  @Input() flyCutDisabled = false;
  /** Empty (default) hides the help icon — only FlyPlates rows show one on this field. */
  @Input() flyCutHelpText = "";

  // "Try it out" button
  @Input() showTryItOut = true;
  /** Pass-through for a host's tour anchor (e.g. dashboard's `step7`) — kept as an
   * Input rather than moved into host markup since the button itself is owned here. */
  @Input() tryItOutTourAnchor: string | null = null;
  @Input() tryItOutHeapAttr = "";
  @Output() tryItOut = new EventEmitter<void>();

  // Title/Description field
  @Input() titleValue = "";
  @Output() titleValueChange = new EventEmitter<string>();
  @Input() titleLabel = "Title/Description";
  @Input() titlePlaceholder = "Title/Description";
  @Input() titleRequired = true;
  @Input() titleError = "";
  @Input() titleDisabled = false;
  @Input() titleHelpText =
    "Your Title/Description is a quick reference reminder of the content inside of the FlyMSG or of your applicable use case. This area is searchable via the search bar in addition to content in your FlyMSG.";

  // Version/usage column (the clock + count block repeated inside the body)
  @Input() showVersionColumn = true;
  @Input() versionCount = 0;
  @Input() versionLabel = "Versions";
  @Output() versionClick = new EventEmitter<void>();

  // Action select
  @Input() showActionSelect = true;
  @Input() actionOptions: VngSelectOption[] = [];
  @Output() actionSelected = new EventEmitter<string>();

  // "FlyMSG" label above the projected editor
  @Input() showEditorLabel = true;
  @Input() editorLabel = "FlyMSG";
  @Input() editorHelpText =
    "The text you create here will automatically replace your FlyCut that you type whenever and wherever you use it.";

  // Per-row validation errors (e.g. failed save)
  @Input() errors: string[] = [];

  // Autosave status line
  @Input() showSavingNote = true;
  @Input() isSaving = false;
  @Input() savingStatusText = "Saving changes...";
  @Input() savingReminderText = "Don't forget to test out your FlyCut in the Preview Panel.";

  // Duplicate / Change Category / Delete footer
  @Input() showDefaultActions = true;
  /**
   * "Duplicate FlyMSG" only makes sense for a row that is already saved —
   * settings' Import FlyCuts preview edits records that do not exist yet, so it
   * turns this off and keeps Change Category + Delete.
   */
  @Input() showDuplicate = true;
  /**
   * "Change Category" is likewise off for the Import FlyCuts preview: the
   * category picker there wrote to a record the import POST never reads, so the
   * button did nothing.
   */
  @Input() showChangeCategory = true;
  @Output() duplicate = new EventEmitter<void>();
  @Output() changeCategory = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();

  onFlyCutInput(value: string): void {
    this.flyCutValue = value;
    this.flyCutValueChange.emit(value);
  }

  onTitleInput(value: string): void {
    this.titleValue = value;
    this.titleValueChange.emit(value);
  }

  onActionSelection(value: string): void {
    this.actionSelected.emit(value);
  }
}
